// ============================================================
// web/src/lib/useResource.js — LE hook de chargement.
// ------------------------------------------------------------
// Un seul hook pour le triptyque chargement / erreur / données.
// Pas quinze implémentations différentes : chez AGC chaque écran
// refaisait son propre useEffect, et aucun ne traitait le cas de
// l'erreur de la même façon.
//
// Règle associée : en cas d'erreur, `data` reste `null`. JAMAIS de
// repli sur des données inventées — un état d'erreur honnête.
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * @param {() => Promise<T>} loader  doit être stable (useCallback)
 * @param {Array} deps
 * @returns {{data: T|null, error: Error|null, loading: boolean, reload: () => void}}
 * @template T
 */
export function useResource(loader, deps = []) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const [tick, setTick] = useState(0);
  // Évite d'écrire dans un composant démonté, et surtout d'afficher le
  // résultat d'une requête périmée par-dessus une plus récente.
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    loader()
      .then((data) => {
        if (cancelled || id !== requestId.current) return;
        setState({ data, error: null, loading: false });
      })
      .catch((error) => {
        if (cancelled || id !== requestId.current) return;
        if (error?.name === 'AbortError') return;
        setState({ data: null, error, loading: false });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/**
 * Action déclenchée par l'utilisateur (enregistrer, supprimer).
 * Sépare l'état « en cours » de l'état de chargement d'écran : un bouton
 * qui reste actif pendant son propre traitement produit des doubles
 * soumissions.
 */
export function useAction(action) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(
    async (...args) => {
      setPending(true);
      setError(null);
      try {
        return await action(...args);
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setPending(false);
      }
    },
    [action],
  );

  return { run, pending, error, clearError: () => setError(null) };
}
